import {ChangeDetectionStrategy, Component, OnInit, ViewContainerRef} from '@angular/core';
import {Router} from '@angular/router';
import {filter, map, startWith, switchMap, take, withLatestFrom} from 'rxjs/operators';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {Title} from '@angular/platform-browser';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {Location} from '@angular/common';

import {UnitPaymentService} from '../unit-payment.service';
import {PaymentType} from '../../../../enums/payment-type.enum';
import {LoggerService} from '../../../../errors/logger.service';
import {RoutingService} from '../../../../../services/routing.service';
import {UnitPaymentStatus} from '../models/unit-payment-status.enum';
import {PhoneVerificationDialogContext} from '../../../phone-verification-dialog/PhoneVerificationDialogContext';
import {PhoneVerificationDialogComponent} from '../../../phone-verification-dialog/phone-verification-dialog.component';
import {BasePhoneVerificationStrategy} from '../../../../models/phone-verification-strategies/base-phone-verification-strategy.model';
import {TerraUtils} from '../../../../TerraUtils';
import TermType from '../../../../enums/TermsTypes.enum';

@Component({
  selector: 'terra-unit-payment-review',
  templateUrl: './unit-payment-review.component.html',
  styleUrls: ['./unit-payment-review.component.scss', '../../unit-shared.scss'],
  providers: [
    UnitPaymentService,
    {provide: BasePhoneVerificationStrategy, useClass: UnitPaymentService}
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnitPaymentReviewComponent implements OnInit {
  /**
   * Changes when this component executes HTTP request, effects the loader on the HTML.
   */
  private _isLoading$ = new BehaviorSubject<boolean>(false);
  payment$ = this.paymentService.payment$;
  sourceBankAccount$ = this.paymentService.sourceBankAccount$;
  isLoading$ = combineLatest([this.payment$.pipe(startWith(null)), this.sourceBankAccount$.pipe(startWith(null)), this._isLoading$]).pipe(
    map(([payment, sourceBankAccount, isLoading]) => !payment || !sourceBankAccount || isLoading)
  );
  agreedToTerms$ = new BehaviorSubject<boolean>(false);
  generalError$ = new BehaviorSubject<boolean>(false);
  bankAccountBalanceErrorMessage$ = new BehaviorSubject<string>('');
  serverErrorMessage$ = new BehaviorSubject<string>('');
  isWirePayment$ = this.payment$.pipe(
    map(payment => payment.paymentType === PaymentType.Wire)
  );
  
  acceptTransactionFee:boolean = false;
  CrePaymentType = PaymentType;
  termType = TermType;
  isGp:boolean;
  paymentAmount: number;
  paymentFee: number;

  constructor(private paymentService: UnitPaymentService,
              private routingService: RoutingService,
              private router: Router,
              private logger: LoggerService,
              private vcr: ViewContainerRef,
              private dialog: MatDialog,
              private titleService: Title,
              private _location: Location) {
                this.isGp = paymentService.isGp;
  }

  ngOnInit(): void {
    this.payment$.pipe(
      take(1),
      filter(payment => !!payment)
    ).subscribe(payment => {
      this.paymentAmount = payment.amount;
      this.paymentFee = payment.fee;
      if (payment.status === UnitPaymentStatus.Sent) {
        this.navigateToConfirmation(payment.id);
      }
    });
    this.titleService.setTitle('Payment Review' + TerraUtils.consts.GpPageTitleSuffix);
  }

  get bankAccountPage$(): Observable<string> {
    return this.sourceBankAccount$.pipe(
      take(1),
      map(bankAccount => {
        if ((bankAccount.balance / 100) - this.paymentAmount < this.paymentFee){
          this.bankAccountBalanceErrorMessage$.next(
            `Insufficient funds - The total amount exceeds your current balance of $${(bankAccount.balance / 100)}.`
          )
        }
        if (this.paymentService.isGp) {
          return this.routingService.creBankAccountPage(bankAccount.id)
        }
        else {
          return this.routingService.lpUnitBankAccount(bankAccount.id)
        }
      })
    );
  }

  get editPaymentPage$(): Observable<string> {
    return this.payment$.pipe(
      take(1),
      map(payment => {
        if (this.paymentService.isGp) {
          return this.routingService.paymentPage(payment.id)
        }
        else {
          return this.routingService.lpUnitPaymentPage(payment.id)
        }
      })
    );
  }

  private submitPayment() {
    this._isLoading$.next(true);
    this.generalError$.next(false);
    this.serverErrorMessage$.next('');
    this.paymentService.confirmPayment().pipe(
      take(1),
    ).subscribe(
      payment => {
        this._isLoading$.next(false);
        this.navigateToConfirmation(payment.id);
      },
      error => {
        this.logger.error('An error occurred sending a payment from Unit CRE bank account.', error);
        this._isLoading$.next(false);
        if (!!error.error && Array.isArray(error.error)) {
          const errorMessage = 'Please fix the following issues:\n\t' + error.error.join('\n\t');
          this.serverErrorMessage$.next(errorMessage);
        } else {
          this.generalError$.next(true);
        }
      });
  }

  private navigateToConfirmation(paymentId: number) {
    if (this.paymentService.isGp) {
      this.router.navigateByUrl(this.routingService.paymentConfirmationPage(paymentId));
    }
    else {
      this.router.navigateByUrl(this.routingService.lpUnitPaymentConfirmationPage(paymentId));
    }
  }

  public verifyPhone() {
    this.payment$.pipe(
      take(1),
      switchMap(payment => {
        if (payment.status === UnitPaymentStatus.OtpVerified) {
          return of(true);
        }
        const dialogConfig = new MatDialogConfig<PhoneVerificationDialogContext>();
        dialogConfig.data = new PhoneVerificationDialogContext();
        dialogConfig.data.phone = payment.phoneVerificationNumber;
        dialogConfig.data.isUserAbleToEditPhoneNumber = false;
        dialogConfig.viewContainerRef = this.vcr;
        dialogConfig.disableClose = true;
        return this.dialog.open(PhoneVerificationDialogComponent, dialogConfig).afterClosed();
      }),
      withLatestFrom(this.payment$)
    ).subscribe(([isVerified, payment]) => {
      if (isVerified) {
        // update the status here in case submitting payment fails so the user won't be asked to verify phone again
        payment.status = UnitPaymentStatus.OtpVerified;
        this.submitPayment();
      }
    });
  }

  goBack() {
    this._location.back();
  }
}
